import React, { useState, useEffect } from 'react';
import { IconEdit, IconPlus } from '../icons';
import { comum } from '../../styles/Themes/light';
import ButtonDefault from '../form/ButtonDefault';

import {
  Container,
  SelectorButton,
  Modal,
  ModalContent,
  ModalHeader,
  CloseButton,
  SearchContainer,
  OfficeList,
  OfficeItem,
  NoResultsMessage
} from './styles';
import api from '../../services/api';
import toast from 'react-hot-toast';

export interface Office {
  id_escritorio_solicitante: number;
  nome: string;
  codigo: number;
}

interface OfficeSelectorProps {
  selectedOffice: Office | null;
  onSelectOffice: (id_escritorio_solicitante?: number) => void;
  error?: string;
  id_proposta: number;
  placeholder?: string;
  className?: string;
}

const OfficeSelector: React.FC<OfficeSelectorProps> = ({
  selectedOffice,
  onSelectOffice,
  error,
  id_proposta,
  placeholder = "Adicionar Solicitante",
  className
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [offices, setOffices] = useState<Office[]>([]);
  const [loading, setLoading] = useState(false);
  const [office, setOffice] = useState<Office | null>(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const toastDef= {
      duration: 3000,
      className: 'toasted',
      style: {
          padding: '16px',
          color: comum.colors.interfaceGray400,
      },
      iconTheme: {
          primary: comum.colors.interfaceSuccess,
          secondary: 'var(--cfffaee)',
      },
  }

  const toastDefError= {
    duration: 3000,
    className: 'toasted',
    style: {
        padding: '16px',
        color: comum.colors.interfaceText100,
    },
    iconTheme: {
        primary: comum.colors.primaryRed400,
        secondary: 'var(--cfffaee)',
    },
}


  async function carregarEscritorios() {
    const response = await api.get('/escritorio/ativos');
    const escritorios = response.data.result;
    setOffices(escritorios);
  }

  useEffect(() => {
    if (isModalOpen) {
      setLoading(true);
      carregarEscritorios();
      setLoading(false);
    }
  }, [isModalOpen]);

  const filteredOffices = offices.filter(office =>
    office.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    office.codigo && office.codigo.toString().includes(searchTerm.toLowerCase())
  );

  const handleOfficeSelect = (office: Office) => {
    setOffice(office);
    setConfirmModalOpen(true);
  };

  const handleClearSelection = () => {
    setOffice(null);
    setIsModalOpen(false);
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSearchTerm('');
  };

  const closeConfirmModal = () => {
    setConfirmModalOpen(false);
  };

  const handleConfirmSelection = async () => {

    try {

      const response = await api.put(`/proposta/${id_proposta}/escritorio/${office?.id_escritorio_solicitante}`);

      if(response.data.status !== 'success') {
        throw new Error(response.data.result[0].message);
      }

      toast.success("Escritório Solicitante selecionado com sucesso", toastDef);
      closeConfirmModal();
      closeModal();
      setIsModalOpen(false);
      setSearchTerm('');
      setTimeout(() => {
        onSelectOffice(office?.id_escritorio_solicitante);
      }, 2000);
    } catch (error: any) {
      toast.error("Erro ao selecionar escritório solicitante. " + error.message, toastDefError);
    }

    return
  };

  return (
    <Container className={className}>
      <SelectorButton
        hasSelection={!!selectedOffice}
        className={error ? 'error' : ''}
        onClick={openModal}
      >
        {selectedOffice ? (
          <>
            <div className="selected-office">
              <span className="office-name">{selectedOffice.nome}</span>
              <div className="office-codes">
                <span className="code-new">ES{selectedOffice.id_escritorio_solicitante}</span>
                {selectedOffice.codigo&& (
                  <span className="code-old">({selectedOffice.codigo})</span>
                )}
              </div>
            </div>
            <div className="edit-icon">
              <IconEdit color={comum.colors.interfaceText300} />
            </div>
          </>
        ) : (
          <>
            <IconPlus color={comum.colors.interfacePure0} />
            {placeholder}
          </>
        )}
      </SelectorButton>

      {error && <div className="error-text">{error}</div>}

      {isModalOpen && (
        <Modal onClick={closeModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>Selecionar Escritório</h3>
              <CloseButton onClick={closeModal}>×</CloseButton>
            </ModalHeader>

            <SearchContainer>
              <input
                type="text"
                placeholder="Buscar por nome ou código..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
            </SearchContainer>

            <OfficeList>
              {loading ? (
                <div className="loading">Carregando escritórios...</div>
              ) : filteredOffices.length > 0 ? (
                filteredOffices.map((office) => (
                  <OfficeItem
                    key={office.id_escritorio_solicitante}
                    isSelected={selectedOffice?.id_escritorio_solicitante === office.id_escritorio_solicitante}
                    onClick={() => handleOfficeSelect(office)}
                  >
                    <div className="office-info">
                      <div className="office-name">{office.nome}</div>
                      <div className="office-codes">
                        <span className="code-label">Código:</span>
                        <span className="code-new">ES{office.id_escritorio_solicitante}</span>
                        {office.codigo && (
                          <>
                            <span className="code-separator">•</span>
                            <span className="code-old">{office.codigo}</span>
                          </>
                        )}
                      </div>
                    </div>
                    {selectedOffice?.id_escritorio_solicitante === office.id_escritorio_solicitante && (
                      <div className="selected-tag">Selecionado</div>
                    )}
                  </OfficeItem>
                ))
              ) : (
                <NoResultsMessage>
                  Nenhum escritório encontrado para "{searchTerm}"
                </NoResultsMessage>
              )}
            </OfficeList>

            {selectedOffice && (
              <div className="modal-footer">
                <button className="clear-button" onClick={handleClearSelection}>
                  Limpar Seleção
                </button>
              </div>
            )}
          </ModalContent>
        </Modal>
      )}
      {confirmModalOpen && (
        <Modal onClick={closeConfirmModal}>
          <ModalContent onClick={(e) => e.stopPropagation()} className="confirm-modal">
            <ModalHeader>
              <h3>Selecionar Solicitante</h3>
              <CloseButton onClick={closeConfirmModal}>×</CloseButton>
            </ModalHeader>
            <h3 className="p-6">Deseja realmente selecionar este solicitante?
              <br></br>
              <br></br>
              <span className="text-sm text-gray-500">
                {office?.nome}
              </span>
              <br></br>
              <br></br>
              Esta ação não poderá ser desfeita.
            </h3>
            <div className="modal-footer">
              <ButtonDefault className="cancel" type="button" onClick={closeConfirmModal}>
                Cancelar
              </ButtonDefault>

              <ButtonDefault className="continue" type="button" onClick={handleConfirmSelection}>
                Confirmar
              </ButtonDefault>
            </div>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default OfficeSelector; 